/**
 * SVG overlay layer drawn ON TOP of the LiveKit video. Coordinates are
 * normalised [0, 1]; viewBox + preserveAspectRatio="none" lets the same
 * data render at any viewport size without distortion concerns.
 *
 * Two modes:
 *   - readOnly: render persisted annotations only (field side at v1).
 *   - editable: also handles pointer events to draw new annotations and
 *     publishes them via the sync helpers.
 *
 * Stroke widths are NORMALISED too — a `strokeWidth=0.006` in this
 * viewBox renders as 0.6% of the SVG's pixel width (≈4–8 visible px on
 * typical viewports). We deliberately do NOT use
 * `vector-effect="non-scaling-stroke"` here: that flag would reinterpret
 * strokeWidth as raw pixels, making 0.006 render at 0.006 px — invisible.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  buildDraft,
  newAnnotationId,
  pointsToPath,
  toolToKind,
  useAnnotationStore,
} from "@/lib/annotation";
import { publishAnnotation } from "@/lib/annotationSync";
import { findTargetVideo } from "@/lib/annotationTarget";
import type { CommandBus, EnvelopeIdentity } from "@/lib/commandBus";
import {
  makeDraftBroadcaster,
  useLiveDraftsStore,
} from "@/lib/liveDrafts";
import type {
  Annotation,
  ArrowGeometry,
  EllipseGeometry,
  FreehandGeometry,
  PolylineGeometry,
  RectGeometry,
  TextGeometry,
} from "@/lib/types";

type Props = {
  sessionId: string;
  /** When true, pointer events draw + publish; otherwise render-only. */
  editable: boolean;
  bus: CommandBus | null;
  /** Operator/participant identity stamped on outbound envelopes. */
  self: EnvelopeIdentity;
};

// Phase 1B-polish-drafts — six in-progress geometry shapes.
// Polyline's `cur` is the hovering pointer's current position;
// finalisation drops it via `onDoubleClick`. Arrow mirrors rect's
// drag-shape but the final geometry is {x1,y1,x2,y2}.
type LiveDraft =
  | { kind: "freehand"; points: [number, number][]; color: string }
  | { kind: "rect"; start: [number, number]; cur: [number, number]; color: string }
  | { kind: "ellipse"; start: [number, number]; cur: [number, number]; color: string }
  | { kind: "arrow"; start: [number, number]; cur: [number, number]; color: string }
  | {
      kind: "polyline";
      points: [number, number][];
      cur: [number, number];
      color: string;
    };

const DRAFT_STROKE_WIDTH = 0.006;

type VideoBox = { left: number; top: number; width: number; height: number };

export function AnnotationOverlay({ sessionId, editable, bus, self }: Props) {
  const { t } = useTranslation();
  const annotations = useAnnotationStore((s) => s.byId);
  const tool = useAnnotationStore((s) => s.tool);
  const color = useAnnotationStore((s) => s.color);
  // Peer drafts (live in-progress strokes broadcast over the unreliable
  // channel by whoever is currently dragging). Filter to live-overlay
  // scope (evidence_id === null) so collab-annotator drafts don't leak
  // into the live video overlay.
  const liveDrafts = useLiveDraftsStore((s) => s.byId);
  const peerDrafts = useMemo(
    () => Object.values(liveDrafts).filter((d) => d.evidence_id === null),
    [liveDrafts],
  );

  const svgRef = useRef<SVGSVGElement | null>(null);
  const [draft, setDraft] = useState<LiveDraft | null>(null);
  // Phase 1B-polish-text-editor — inline SVG <foreignObject> input
  // open at the click point for the text tool. null = no editor open.
  // Enter commits, Esc cancels (handled by the input's keydown).
  const [textEdit, setTextEdit] = useState<{ x: number; y: number; value: string } | null>(null);
  // Broadcaster for the in-flight gesture. Reset on each pointerdown
  // with a fresh id so the eventual `draw.create` envelope's id matches
  // the draft id and the peer can supersede the draft cleanly.
  const broadcasterRef = useRef<ReturnType<typeof makeDraftBroadcaster> | null>(
    null,
  );
  const draftIdRef = useRef<string | null>(null);
  // Pixel bounds of the *visible video frame* within our positioning
  // parent — recomputed whenever the video element resizes, its
  // intrinsic dimensions change, or its container reflows. Normalised
  // [0, 1] annotation coords map onto these bounds, so what the
  // operator draws on a 16:9 desktop tile lands at the same visual
  // point on a 9:16 mobile portrait tile (where the video is
  // letterboxed via object-fit: contain).
  const [videoBox, setVideoBox] = useState<VideoBox | null>(null);
  // LiveKit applies `transform: scaleX(-1)` to the local participant's
  // selfie view by default, so the field user sees their own camera
  // mirrored. Without applying the same mirror to the SVG, annotations
  // drawn at video pixel (X, Y) by the operator land on the OPPOSITE
  // horizontal side on the field's screen — visually pointing at the
  // wrong content. We detect the video's computed transform and apply
  // the same to the SVG when needed.
  const [mirrorX, setMirrorX] = useState<boolean>(false);

  const list = useMemo(() => Object.values(annotations), [annotations]);

  // Track the camera video element (the field's stream — local on the
  // field side, remote on the operator side) and re-derive its visible
  // bounds whenever anything resizes. The visible bounds = the
  // letterboxed area inside the <video> element, accounting for
  // object-fit: contain (LiveKit's default).
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const parent = svg.parentElement;
    if (!parent) return;

    function pickVideo(): HTMLVideoElement | null {
      // Delegate to the pure helper so the selector preference has
      // its own unit-test coverage (see test/annotationTarget.test.ts).
      return findTargetVideo({ doc: document, editable });
    }

    function update() {
      const svgNow = svgRef.current;
      const parentNow = svgNow?.parentElement;
      if (!svgNow || !parentNow) return;
      const video = pickVideo();
      if (!video) {
        setVideoBox(null);
        return;
      }
      const parentRect = parentNow.getBoundingClientRect();
      const videoRect = video.getBoundingClientRect();
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      // Sync the SVG's horizontal mirror state with the video's
      // computed transform. `scaleX(-1)` becomes `matrix(-1, 0, 0, 1,
      // 0, 0)`; a negative first matrix component indicates horizontal
      // flip. We re-read this on every update — LiveKit can toggle the
      // mirror per device (e.g. when switching to the rear camera the
      // selfie mirror is dropped).
      const style = window.getComputedStyle(video);
      const transform = style.transform;
      let mirrored = false;
      if (transform && transform !== "none") {
        const m = transform.match(/matrix(?:3d)?\(([^,]+),/);
        if (m && m[1]) {
          const a = parseFloat(m[1]);
          if (Number.isFinite(a) && a < 0) mirrored = true;
        }
      }
      setMirrorX(mirrored);

      // Until metadata is loaded we don't know the intrinsic aspect.
      // Fall back to the element's full rect; close enough for a few
      // hundred ms until `loadedmetadata` fires and we recompute.
      if (!vw || !vh) {
        setVideoBox({
          left: videoRect.left - parentRect.left,
          top: videoRect.top - parentRect.top,
          width: videoRect.width,
          height: videoRect.height,
        });
        return;
      }
      const videoAspect = vw / vh;
      const elemAspect = videoRect.width / videoRect.height;
      let drawW: number;
      let drawH: number;
      let drawX: number;
      let drawY: number;
      if (videoAspect > elemAspect) {
        // Video is wider than its element → letterbox top + bottom.
        drawW = videoRect.width;
        drawH = videoRect.width / videoAspect;
        drawX = 0;
        drawY = (videoRect.height - drawH) / 2;
      } else {
        // Video is taller than its element → letterbox left + right.
        drawW = videoRect.height * videoAspect;
        drawH = videoRect.height;
        drawX = (videoRect.width - drawW) / 2;
        drawY = 0;
      }
      setVideoBox({
        left: videoRect.left + drawX - parentRect.left,
        top: videoRect.top + drawY - parentRect.top,
        width: drawW,
        height: drawH,
      });
    }

    update();

    const observer = new ResizeObserver(update);
    observer.observe(parent);
    let currentVideo: HTMLVideoElement | null = null;
    function attach() {
      const video = pickVideo();
      if (video === currentVideo) return;
      if (currentVideo) {
        observer.unobserve(currentVideo);
        currentVideo.removeEventListener("loadedmetadata", update);
        currentVideo.removeEventListener("resize", update);
      }
      currentVideo = video;
      if (currentVideo) {
        observer.observe(currentVideo);
        currentVideo.addEventListener("loadedmetadata", update);
        currentVideo.addEventListener("resize", update);
      }
    }
    attach();
    // LiveKit may swap the <video> element when tracks (re)publish —
    // poll occasionally to catch a replacement and re-attach.
    const interval = window.setInterval(() => {
      attach();
      update();
    }, 1000);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
      window.clearInterval(interval);
      if (currentVideo) {
        currentVideo.removeEventListener("loadedmetadata", update);
        currentVideo.removeEventListener("resize", update);
      }
    };
  }, [editable]);

  function pointFromEvent(e: PointerEvent | React.PointerEvent): [number, number] | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    return [clamp(x), clamp(y)];
  }

  // The overlay only intercepts pointer events when the operator has
  // explicitly armed a draw tool (tool !== "off"). Otherwise it stays
  // transparent to clicks so LiveKit's bottom controls (mute / leave /
  // share / settings) are reachable.
  const armed = editable && tool !== "off";

  async function publishFromDraft(
    geometry:
      | FreehandGeometry
      | RectGeometry
      | EllipseGeometry
      | ArrowGeometry
      | PolylineGeometry
      | TextGeometry,
    color: string,
    id: string | null,
  ) {
    const kind = toolToKind(tool);
    const draftPayload = buildDraft(kind, geometry, color);
    try {
      await publishAnnotation(bus, self, sessionId, {
        kind: draftPayload.kind,
        geometry: draftPayload.vector_jsonb,
        color: draftPayload.color,
        stroke_width: draftPayload.stroke_width,
        ...(id ? { id } : {}),
      });
    } catch {
      /* server rejected — the overlay reflects only persisted rows so
         nothing local to undo */
    }
  }

  function onPointerDown(e: React.PointerEvent) {
    if (!armed) return;
    const p = pointFromEvent(e);
    if (!p) return;

    // ---- Polyline: accumulating clicks ----
    // First click opens a draft; subsequent clicks append a vertex.
    // Don't preventDefault on these so the same click can fire the
    // browser's dblclick detector for finalisation.
    if (tool === "polyline") {
      if (draft && draft.kind === "polyline") {
        setDraft((prev) => {
          if (!prev || prev.kind !== "polyline") return prev;
          const next: LiveDraft = {
            ...prev,
            points: [...prev.points, p],
            cur: p,
          };
          broadcasterRef.current?.send(draftGeometry(next));
          return next;
        });
        return;
      }
      const id = newAnnotationId();
      draftIdRef.current = id;
      broadcasterRef.current = makeDraftBroadcaster(bus, {
        id,
        evidence_id: null,
        kind: "polyline",
        color,
        stroke_width: DRAFT_STROKE_WIDTH,
      });
      setDraft({ kind: "polyline", points: [p], cur: p, color });
      return;
    }

    // ---- Text: open an inline editor at the click point ----
    // Phase 1B-polish-text-editor — replaces the v1 window.prompt.
    // A second click while one's already open commits the previous
    // value first (the input's onBlur handler covers that).
    if (tool === "text") {
      e.preventDefault();
      setTextEdit({ x: p[0], y: p[1], value: "" });
      return;
    }

    // ---- Freehand / rect / ellipse / arrow: drag gesture ----
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    // Mint the future annotation id NOW so live drafts and the
    // eventual persisted row share an id. Peer can supersede the
    // transient draft with the persisted row by id match.
    const id = newAnnotationId();
    draftIdRef.current = id;
    broadcasterRef.current = makeDraftBroadcaster(bus, {
      id,
      evidence_id: null,
      kind: toolToKind(tool),
      color,
      stroke_width: DRAFT_STROKE_WIDTH,
    });
    if (tool === "pen") {
      setDraft({ kind: "freehand", points: [p], color });
    } else if (tool === "rect") {
      setDraft({ kind: "rect", start: p, cur: p, color });
    } else if (tool === "ellipse") {
      setDraft({ kind: "ellipse", start: p, cur: p, color });
    } else if (tool === "arrow") {
      setDraft({ kind: "arrow", start: p, cur: p, color });
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!armed) return;
    const p = pointFromEvent(e);
    if (!p) return;
    // Functional updates — pointermove fires faster than React commits, so
    // a closure-captured `draft` is stale and freehand strokes would only
    // ever record (start, latest_point) instead of the full gesture.
    setDraft((prev) => {
      if (!prev) return prev;
      let next: LiveDraft;
      if (prev.kind === "freehand") {
        next = { ...prev, points: [...prev.points, p] };
      } else if (prev.kind === "polyline") {
        // Show the hovering segment from the last placed vertex to the
        // pointer; the user finalises by double-click.
        next = { ...prev, cur: p };
      } else {
        next = { ...prev, cur: p };
      }
      // Throttled live broadcast so the peer sees the stroke being drawn.
      // Failures are swallowed inside the broadcaster (unreliable channel).
      broadcasterRef.current?.send(draftGeometry(next));
      return next;
    });
  }

  async function onPointerUp() {
    if (!armed || !draft) return;
    // Polyline + text don't finalise on pointerup — polyline waits for
    // dblclick, text is published synchronously by pointerdown.
    if (draft.kind === "polyline") return;
    const finalised = draft;
    const draftId = draftIdRef.current;
    setDraft(null);
    draftIdRef.current = null;
    broadcasterRef.current = null;

    if (finalised.kind === "freehand" && finalised.points.length < 2) return;
    if (
      (finalised.kind === "rect" || finalised.kind === "ellipse") &&
      pointsAlmostEqual(finalised.start, finalised.cur)
    ) {
      return;
    }
    if (
      finalised.kind === "arrow" &&
      pointsAlmostEqual(finalised.start, finalised.cur)
    ) {
      return;
    }

    const geometry = draftGeometry(finalised);
    await publishFromDraft(geometry, finalised.color, draftId);
  }

  async function onDoubleClick(e: React.MouseEvent) {
    // Polyline finalisation. Drop the trailing "hover" point — only the
    // explicitly-clicked vertices end up in the persisted geometry.
    if (!armed || !draft || draft.kind !== "polyline") return;
    e.preventDefault();
    const draftId = draftIdRef.current;
    const points = draft.points;
    const color = draft.color;
    setDraft(null);
    draftIdRef.current = null;
    broadcasterRef.current = null;
    if (points.length < 2) return; // polyline needs >=2 vertices server-side
    await publishFromDraft(
      { points } satisfies PolylineGeometry,
      color,
      draftId,
    );
  }

  function onPointerCancel() {
    // Tell peers to drop the in-flight draft NOW; otherwise it sits
    // there until the 5s prune timer ages it out.
    broadcasterRef.current?.end();
    draftIdRef.current = null;
    broadcasterRef.current = null;
    setDraft(null);
  }

  // Reset draft if the tool changes mid-drag.
  useEffect(() => {
    setDraft(null);
  }, [tool]);

  // The SVG is sized + positioned to overlay EXACTLY the visible video
  // frame (computed in the useEffect above). When videoBox is null —
  // metadata not yet known, no camera in the room — fall back to a
  // full-container overlay so the toolbar still works on first paint.
  // Always pointer-events: none on non-armed mode so LiveKit controls
  // (mute / leave / share, plus the in-room control slot rendered on
  // top in z-30) remain clickable.
  const style: React.CSSProperties = videoBox
    ? {
        position: "absolute",
        left: `${videoBox.left}px`,
        top: `${videoBox.top}px`,
        width: `${videoBox.width}px`,
        height: `${videoBox.height}px`,
        // Match the video's horizontal flip when LiveKit applies the
        // selfie mirror. The pointer-event math below ALSO has to
        // account for this so the operator draws where the visual
        // content is, not where the un-mirrored pixels are.
        transform: mirrorX ? "scaleX(-1)" : undefined,
      }
    : { position: "absolute", inset: 0, bottom: 88 };
  return (
    <svg
      ref={svgRef}
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      style={style}
      className={
        armed
          ? "pointer-events-auto z-20 cursor-crosshair touch-none"
          : "pointer-events-none z-20"
      }
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onDoubleClick={onDoubleClick}
      data-testid="annotation-overlay"
    >
      {list.map((a) => (
        <RenderedAnnotation key={a.id} a={a} />
      ))}
      {/* Peer's in-progress strokes. Identical visual to a finalized
          stroke so when `draw.create` lands and the draft is removed,
          the persisted row takes its place with no flicker. */}
      {peerDrafts.map((d) => (
        <PeerDraftAnnotation key={d.id} d={d} />
      ))}
      {draft && <DraftAnnotation draft={draft} />}
      {/* Phase 1B-polish-text-editor — inline editor for the text tool.
          Renders inside the SVG via <foreignObject> so it scales with
          the rest of the overlay; the input is a real HTML <input> so
          keyboard input + IME composition work natively. */}
      {textEdit && (
        <foreignObject
          x={textEdit.x}
          y={textEdit.y}
          width={0.4}
          height={0.06}
          style={{ overflow: "visible" }}
        >
          <input
            type="text"
            autoFocus
            value={textEdit.value}
            placeholder={t("annotation.textLabelPlaceholder")}
            data-testid="annotation-text-input"
            style={{
              // Tiny font-size in browser pixels relative to the
              // viewBox unit. 0.03 viewBox-units ≈ ~24px at 800px wide.
              fontSize: "0.024px",
              padding: "0.004px 0.006px",
              border: `0.001px solid ${color}`,
              background: "rgba(0,0,0,0.6)",
              color,
              borderRadius: "0.004px",
              outline: "none",
              width: "100%",
              boxSizing: "border-box",
            }}
            onChange={(ev) =>
              setTextEdit((prev) =>
                prev ? { ...prev, value: ev.target.value } : prev,
              )
            }
            onKeyDown={(ev) => {
              if (ev.key === "Enter") {
                ev.preventDefault();
                const v = textEdit.value.trim();
                if (v) {
                  void publishFromDraft(
                    {
                      x: textEdit.x,
                      y: textEdit.y,
                      text: v,
                    } satisfies TextGeometry,
                    color,
                    newAnnotationId(),
                  );
                }
                setTextEdit(null);
              } else if (ev.key === "Escape") {
                ev.preventDefault();
                setTextEdit(null);
              }
            }}
            onBlur={() => {
              // Commit on blur so clicking elsewhere finalises rather
              // than silently dropping the typed text.
              const v = textEdit.value.trim();
              if (v) {
                void publishFromDraft(
                  {
                    x: textEdit.x,
                    y: textEdit.y,
                    text: v,
                  } satisfies TextGeometry,
                  color,
                  newAnnotationId(),
                );
              }
              setTextEdit(null);
            }}
          />
        </foreignObject>
      )}
    </svg>
  );
}


function RenderedAnnotation({ a }: { a: Annotation }) {
  const stroke = a.color;
  const sw = a.stroke_width;
  if (a.kind === "freehand") {
    const g = a.vector_jsonb as FreehandGeometry;
    return (
      <path
        d={pointsToPath(g.points)}
        fill="none"
        stroke={stroke}
        strokeWidth={sw}
        strokeLinejoin="round"
        strokeLinecap="round"
        aria-label={a.label || `freehand annotation`}
      />
    );
  }
  if (a.kind === "rect") {
    const g = a.vector_jsonb as RectGeometry;
    return (
      <rect
        x={Math.min(g.x, g.x + g.w)}
        y={Math.min(g.y, g.y + g.h)}
        width={Math.abs(g.w)}
        height={Math.abs(g.h)}
        fill="none"
        stroke={stroke}
        strokeWidth={sw}
        aria-label={a.label || `rectangle annotation`}
      />
    );
  }
  if (a.kind === "ellipse") {
    const g = a.vector_jsonb as EllipseGeometry;
    return (
      <ellipse
        cx={g.cx}
        cy={g.cy}
        rx={g.rx}
        ry={g.ry}
        fill="none"
        stroke={stroke}
        strokeWidth={sw}
        aria-label={a.label || `ellipse annotation`}
      />
    );
  }
  if (a.kind === "polyline") {
    const g = a.vector_jsonb as { points: [number, number][] };
    return (
      <polyline
        points={g.points.map((p) => `${p[0]},${p[1]}`).join(" ")}
        fill="none"
        stroke={stroke}
        strokeWidth={sw}
        strokeLinejoin="round"
        strokeLinecap="round"
        aria-label={a.label || `polyline annotation`}
      />
    );
  }
  if (a.kind === "arrow") {
    const g = a.vector_jsonb as { x1: number; y1: number; x2: number; y2: number };
    // Shaft + arrowhead. The head is a small triangle scaled by stroke
    // width so it stays visible at any zoom level.
    const dx = g.x2 - g.x1;
    const dy = g.y2 - g.y1;
    const len = Math.max(Math.hypot(dx, dy), 1e-6);
    const ux = dx / len;
    const uy = dy / len;
    // Perpendicular unit vector for the head's base corners.
    const px = -uy;
    const py = ux;
    const headLen = Math.max(sw * 6, 0.02);
    const headHalfWidth = headLen / 2;
    const baseX = g.x2 - ux * headLen;
    const baseY = g.y2 - uy * headLen;
    return (
      <g aria-label={a.label || `arrow annotation`}>
        <line
          x1={g.x1}
          y1={g.y1}
          x2={baseX}
          y2={baseY}
          stroke={stroke}
          strokeWidth={sw}
          strokeLinecap="round"
        />
        <polygon
          points={[
            `${g.x2},${g.y2}`,
            `${baseX + px * headHalfWidth},${baseY + py * headHalfWidth}`,
            `${baseX - px * headHalfWidth},${baseY - py * headHalfWidth}`,
          ].join(" ")}
          fill={stroke}
          stroke="none"
        />
      </g>
    );
  }
  if (a.kind === "text") {
    const g = a.vector_jsonb as { x: number; y: number; text: string; font_size?: number };
    const fontSize = g.font_size ?? 0.03;
    return (
      <text
        x={g.x}
        y={g.y}
        fill={stroke}
        // SVG <text> in a viewBox=0 0 1 1 — font-size is in viewBox units.
        // `dominantBaseline` keeps the anchor at the top-left of the
        // glyph cap height so authors clicking at (x, y) get the text
        // anchored at their click point.
        fontSize={fontSize}
        dominantBaseline="hanging"
        // Disable the default 1px stroke so glyphs render as solid fill.
        stroke="none"
        aria-label={a.label || `text annotation: ${g.text}`}
      >
        {g.text}
      </text>
    );
  }
  // Unknown kind — render nothing rather than crashing the overlay.
  return null;
}


function PeerDraftAnnotation({
  d,
}: {
  d: import("@/lib/liveDrafts").LiveDraft;
}) {
  if (d.kind === "freehand") {
    const g = d.geometry as FreehandGeometry;
    return (
      <path
        d={pointsToPath(g.points)}
        fill="none"
        stroke={d.color}
        strokeWidth={d.stroke_width}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    );
  }
  if (d.kind === "rect") {
    const g = d.geometry as RectGeometry;
    return (
      <rect
        x={Math.min(g.x, g.x + g.w)}
        y={Math.min(g.y, g.y + g.h)}
        width={Math.abs(g.w)}
        height={Math.abs(g.h)}
        fill="none"
        stroke={d.color}
        strokeWidth={d.stroke_width}
      />
    );
  }
  if (d.kind === "ellipse") {
    const g = d.geometry as EllipseGeometry;
    return (
      <ellipse
        cx={g.cx}
        cy={g.cy}
        rx={g.rx}
        ry={g.ry}
        fill="none"
        stroke={d.color}
        strokeWidth={d.stroke_width}
      />
    );
  }
  // Phase 1B-polish-drafts — peer's in-progress draft for the new kinds.
  if (d.kind === "polyline") {
    const g = d.geometry as PolylineGeometry;
    return (
      <polyline
        points={g.points.map((p) => `${p[0]},${p[1]}`).join(" ")}
        fill="none"
        stroke={d.color}
        strokeWidth={d.stroke_width}
        strokeLinejoin="round"
        strokeLinecap="round"
        strokeDasharray="0.005,0.005"
      />
    );
  }
  if (d.kind === "arrow") {
    const g = d.geometry as ArrowGeometry;
    const sw = d.stroke_width;
    const dx = g.x2 - g.x1;
    const dy = g.y2 - g.y1;
    const len = Math.max(Math.hypot(dx, dy), 1e-6);
    const ux = dx / len;
    const uy = dy / len;
    const px = -uy;
    const py = ux;
    const headLen = Math.max(sw * 6, 0.02);
    const headHalfWidth = headLen / 2;
    const baseX = g.x2 - ux * headLen;
    const baseY = g.y2 - uy * headLen;
    return (
      <g opacity={0.85}>
        <line
          x1={g.x1}
          y1={g.y1}
          x2={baseX}
          y2={baseY}
          stroke={d.color}
          strokeWidth={sw}
          strokeLinecap="round"
        />
        <polygon
          points={[
            `${g.x2},${g.y2}`,
            `${baseX + px * headHalfWidth},${baseY + py * headHalfWidth}`,
            `${baseX - px * headHalfWidth},${baseY - py * headHalfWidth}`,
          ].join(" ")}
          fill={d.color}
          stroke="none"
        />
      </g>
    );
  }
  // Text drafts: peer typing in a prompt — render the current text at
  // its anchor. Same final shape as a persisted text annotation.
  if (d.kind === "text") {
    const g = d.geometry as TextGeometry;
    return (
      <text
        x={g.x}
        y={g.y}
        fill={d.color}
        fontSize={g.font_size ?? 0.03}
        dominantBaseline="hanging"
        stroke="none"
      >
        {g.text}
      </text>
    );
  }
  return null;
}


function DraftAnnotation({ draft }: { draft: LiveDraft }) {
  const stroke = draft.color;
  if (draft.kind === "freehand") {
    return (
      <path
        d={pointsToPath(draft.points)}
        fill="none"
        stroke={stroke}
        strokeWidth={DRAFT_STROKE_WIDTH}
        strokeLinejoin="round"
        strokeLinecap="round"
        opacity={0.9}
      />
    );
  }
  if (draft.kind === "rect") {
    const x = Math.min(draft.start[0], draft.cur[0]);
    const y = Math.min(draft.start[1], draft.cur[1]);
    const w = Math.abs(draft.cur[0] - draft.start[0]);
    const h = Math.abs(draft.cur[1] - draft.start[1]);
    return (
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill="none"
        stroke={stroke}
        strokeWidth={DRAFT_STROKE_WIDTH}
        opacity={0.9}
      />
    );
  }
  if (draft.kind === "ellipse") {
    const cx = (draft.start[0] + draft.cur[0]) / 2;
    const cy = (draft.start[1] + draft.cur[1]) / 2;
    const rx = Math.abs(draft.cur[0] - draft.start[0]) / 2;
    const ry = Math.abs(draft.cur[1] - draft.start[1]) / 2;
    return (
      <ellipse
        cx={cx}
        cy={cy}
        rx={rx}
        ry={ry}
        fill="none"
        stroke={stroke}
        strokeWidth={DRAFT_STROKE_WIDTH}
        opacity={0.9}
      />
    );
  }
  if (draft.kind === "arrow") {
    // Render the same shape as the final (shaft + filled head) so the
    // drag feels live — matches rect/ellipse where the draft and final
    // share geometry, just slightly transparent.
    const sw = DRAFT_STROKE_WIDTH;
    const dx = draft.cur[0] - draft.start[0];
    const dy = draft.cur[1] - draft.start[1];
    const len = Math.max(Math.hypot(dx, dy), 1e-6);
    const ux = dx / len;
    const uy = dy / len;
    const px = -uy;
    const py = ux;
    const headLen = Math.max(sw * 6, 0.02);
    const headHalfWidth = headLen / 2;
    const baseX = draft.cur[0] - ux * headLen;
    const baseY = draft.cur[1] - uy * headLen;
    return (
      <g opacity={0.9}>
        <line
          x1={draft.start[0]}
          y1={draft.start[1]}
          x2={baseX}
          y2={baseY}
          stroke={stroke}
          strokeWidth={sw}
          strokeLinecap="round"
        />
        <polygon
          points={[
            `${draft.cur[0]},${draft.cur[1]}`,
            `${baseX + px * headHalfWidth},${baseY + py * headHalfWidth}`,
            `${baseX - px * headHalfWidth},${baseY - py * headHalfWidth}`,
          ].join(" ")}
          fill={stroke}
          stroke="none"
        />
      </g>
    );
  }
  // polyline — show the placed vertices + the hover-from-last-vertex
  // segment so the user can preview where the next click will go.
  const allPoints: [number, number][] = [...draft.points, draft.cur];
  return (
    <g opacity={0.9}>
      <polyline
        points={allPoints.map((p) => `${p[0]},${p[1]}`).join(" ")}
        fill="none"
        stroke={stroke}
        strokeWidth={DRAFT_STROKE_WIDTH}
        strokeLinejoin="round"
        strokeLinecap="round"
        strokeDasharray="0.005,0.005"
      />
      {draft.points.map((p, i) => (
        <circle
          key={i}
          cx={p[0]}
          cy={p[1]}
          r={DRAFT_STROKE_WIDTH}
          fill={stroke}
        />
      ))}
    </g>
  );
}


function draftGeometry(d: LiveDraft) {
  if (d.kind === "freehand") return { points: d.points } satisfies FreehandGeometry;
  if (d.kind === "rect") {
    return {
      x: Math.min(d.start[0], d.cur[0]),
      y: Math.min(d.start[1], d.cur[1]),
      w: Math.abs(d.cur[0] - d.start[0]),
      h: Math.abs(d.cur[1] - d.start[1]),
    } satisfies RectGeometry;
  }
  if (d.kind === "ellipse") {
    return {
      cx: (d.start[0] + d.cur[0]) / 2,
      cy: (d.start[1] + d.cur[1]) / 2,
      rx: Math.abs(d.cur[0] - d.start[0]) / 2,
      ry: Math.abs(d.cur[1] - d.start[1]) / 2,
    } satisfies EllipseGeometry;
  }
  if (d.kind === "arrow") {
    return {
      x1: d.start[0],
      y1: d.start[1],
      x2: d.cur[0],
      y2: d.cur[1],
    } satisfies ArrowGeometry;
  }
  // polyline — broadcast includes the hovering segment so peers see
  // it grow vertex-by-vertex; the final persisted geometry (after
  // dblclick) drops the trailing hover point.
  return {
    points: [...d.points, d.cur],
  } satisfies PolylineGeometry;
}


function clamp(v: number): number {
  return Math.min(1, Math.max(0, v));
}


function pointsAlmostEqual(a: [number, number], b: [number, number]): boolean {
  return Math.abs(a[0] - b[0]) < 0.002 && Math.abs(a[1] - b[1]) < 0.002;
}
